import { createApi, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { AccountPortfolio, OrdersResponse, PositionsResponse, StrategyStatus } from '../services/api';
import { API_BASE_URL, apiRequest, ApiError } from '../services/http';

const apiBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api) => {
  const request = typeof args === 'string' ? { url: args } : args;
  const endpoint = `${API_BASE_URL.replace(/\/$/, '')}/${request.url.replace(/^\/+/, '')}`;
  const headers = new Headers(request.headers as HeadersInit | undefined);
  let body = request.body as BodyInit | null | undefined;

  if (request.body !== undefined && typeof request.body !== 'string'
    && !(request.body instanceof FormData) && !(request.body instanceof Blob)) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(request.body);
  }

  try {
    const data = await apiRequest(endpoint, {
      method: request.method || 'GET',
      headers,
      body,
      signal: api.signal,
      timeoutMs: 15000,
      retries: 1,
    });
    return { data };
  } catch (error) {
    if (error instanceof ApiError) {
      const data = {
        code: error.code,
        message: error.message,
        request_id: error.requestId,
        details: error.details,
      };
      if (error.statusCode === 0) {
        return {
          error: {
            status: 'CUSTOM_ERROR',
            error: error.message,
            data,
          },
        };
      }
      return {
        error: {
          status: error.statusCode,
          data,
        },
      };
    }
    return {
      error: {
        status: 'FETCH_ERROR',
        error: error instanceof Error ? error.message : String(error),
      },
    };
  }
};

export const tradingApiSlice = createApi({
  reducerPath: 'tradingApi',
  refetchOnFocus: true,
  refetchOnReconnect: true,
  // Account endpoints have bounded broker waits and persisted-state fallbacks;
  // keep their previous 15-second budget while sharing auth/error/retry behavior.
  baseQuery: apiBaseQuery,
  tagTypes: ['Account', 'Orders', 'Strategy'],
  endpoints: (builder) => ({
    positions: builder.query<PositionsResponse, void>({
      query: () => 'account/positions',
      providesTags: ['Account'],
    }),
    portfolio: builder.query<AccountPortfolio, void>({
      query: () => 'account/portfolio',
      providesTags: ['Account'],
    }),
    openOrders: builder.query<OrdersResponse, { page?: number; pageSize?: number }>({
      query: ({ page = 1, pageSize = 100 }) =>
        `account/orders?page=${page}&page_size=${pageSize}`,
      providesTags: ['Orders'],
    }),
    strategyStatus: builder.query<StrategyStatus, 'ON' | 'ORB' | 'DT' | 'NQMM' | 'MORB'>({
      query: (name) => `strategy/status/${name}`,
      providesTags: (_result, _error, name) => [{ type: 'Strategy', id: name }],
    }),
  }),
});

export const {
  usePositionsQuery,
  usePortfolioQuery,
  useOpenOrdersQuery,
  useStrategyStatusQuery,
} = tradingApiSlice;
